import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button, ErrorBanner, Notice, Spinner, TextField } from '../../components/ui';

export const SHOW_DEMO = import.meta.env.VITE_SHOW_DEMO_ACCOUNTS !== 'false';

const DEMO_ACCOUNTS: [string, string, string][] = [
  ['Hotel purchaser', 'purchasing@nileviewhotel.demo', 'Orders above 20k go through two approval levels'],
  ['Hotel approver', 'approver@nileviewhotel.demo', 'Approves the first level'],
  ['Restaurant administrator', 'admin@bistrozamalek.demo', 'Orders without approvals'],
  ['Coffee supplier, sales', 'sales@cairoroasters.demo', 'Quotes RFQs and accepts orders'],
  ['Food supplier, dispatch', 'dispatch@nilefresh.demo', 'Processes and delivers orders'],
  ['Packaging supplier, administrator', 'admin@packright.demo', 'Catalogue and payouts'],
  ['Platform administrator', 'admin@horeca.local', 'Full operator console'],
  ['Platform finance', 'finance@horeca.local', 'Payments, refunds and settlements'],
];

export function AuthLayout({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)]">
      <div className="flex flex-col px-6 py-8 sm:px-10">
        <Link to="/login" className="flex items-center gap-2.5">
          <img src="/favicon.svg" alt="" className="size-8" />
          <span className="cond text-lg font-semibold">HORECA Connect</span>
        </Link>
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[420px]">{children}</div>
        </div>
      </div>
      <aside className="hidden border-s border-line bg-[#20362f] p-10 text-[#e7efe9] lg:flex lg:flex-col">{aside ?? <Pitch />}</aside>
    </div>
  );
}

function Pitch() {
  const steps = ['Request quotes from several suppliers at once', 'Compare prices line by line', 'Route large orders through your approvers', 'Pay, track delivery and get the invoice in one place'];
  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <p className="cond text-[30px] leading-tight font-semibold text-white">Wholesale buying for kitchens, bars and hotels.</p>
        <p className="mt-3 text-[#b9cbc1]">One account for every supplier you buy from — with the approvals and paperwork your finance team expects.</p>
      </div>
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={s} className="flex items-start gap-3">
            <span className="cond num mt-0.5 flex size-6 shrink-0 items-center justify-center rounded border border-[#4f6f62] text-[13px] text-[#cfe0d6]">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post('/auth/login', { email, password });
      await refresh();
      nav(r.otpRequired ? '/verify' : sp.get('next') || '/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout aside={SHOW_DEMO ? <DemoAccounts onPick={(e) => { setEmail(e); setPassword('HorecaDemo2026'); }} /> : undefined}>
      <h1 className="cond text-[28px] font-semibold">Sign in</h1>
      <p className="mt-1 text-ink-2">Use the email your company registered with.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <TextField label="Email" type="email" autoComplete="username" value={email} onChange={setEmail} required />
        <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} required />
        <ErrorBanner error={error} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
      <div className="mt-5 flex justify-between text-[13.5px]">
        <Link to="/forgot-password" className="text-basil hover:underline">
          Forgot password?
        </Link>
        <Link to="/register" className="text-basil hover:underline">
          Register your company
        </Link>
      </div>
    </AuthLayout>
  );
}

function DemoAccounts({ onPick }: { onPick: (email: string) => void }) {
  return (
    <div className="flex h-full flex-col">
      <p className="cond text-[22px] font-semibold text-white">Demo accounts</p>
      <p className="mt-1 text-[13.5px] text-[#b9cbc1]">
        All use the password <code className="rounded bg-white/10 px-1">HorecaDemo2026</code>. Click one to fill the form.
      </p>
      <ul className="mt-5 divide-y divide-white/10 overflow-y-auto">
        {DEMO_ACCOUNTS.map(([role, email, note]) => (
          <li key={email}>
            <button className="w-full py-2.5 text-start hover:text-white" onClick={() => onPick(email)}>
              <span className="block text-[13px] text-[#9fbcae]">{role}</span>
              <span className="block font-medium text-white">{email}</span>
              <span className="block text-[12.5px] text-[#b9cbc1]">{note}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OtpPage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { refresh, me } = useAuth();
  const nav = useNavigate();
  if (me && me.authenticated && !me.pendingOtp) return <Navigate to="/" replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/otp/verify', { code });
      await refresh();
      nav('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1 className="cond text-[28px] font-semibold">Enter your verification code</h1>
      <p className="mt-1 text-ink-2">We emailed a 6-digit code to {me?.user?.email ?? 'you'}. It expires in 10 minutes.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <TextField label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(v) => setCode(v.replace(/\D/g, ''))} required className="[&_input]:num [&_input]:text-lg [&_input]:tracking-[0.3em]" />
        <ErrorBanner error={error} />
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy} disabled={code.length !== 6}>
          Verify and continue
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [step, setStep] = useState<'request' | 'reset' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1 className="cond text-[28px] font-semibold">Reset your password</h1>
      {step === 'request' && (
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              await api.post('/auth/password/forgot', { email });
              setStep('reset');
            });
          }}
        >
          <p className="text-ink-2">We'll email you a 6-digit code.</p>
          <TextField label="Email" type="email" value={email} onChange={setEmail} required />
          <ErrorBanner error={error} />
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
            Send code
          </Button>
        </form>
      )}
      {step === 'reset' && (
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              await api.post('/auth/password/reset', { email, code, newPassword: pw });
              setStep('done');
            });
          }}
        >
          <Notice>If an account exists for {email}, a code is on its way.</Notice>
          <TextField label="Code" inputMode="numeric" maxLength={6} value={code} onChange={(v) => setCode(v.replace(/\D/g, ''))} required />
          <TextField label="New password" type="password" autoComplete="new-password" hint="At least 10 characters with letters and numbers" value={pw} onChange={setPw} required />
          <ErrorBanner error={error} />
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
            Set new password
          </Button>
        </form>
      )}
      {step === 'done' && (
        <div className="mt-6 space-y-4">
          <Notice tone="good">Password changed. Other sessions were signed out.</Notice>
          <Link to="/login" className="text-basil hover:underline">
            Sign in
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const info = useQuery({ queryKey: ['invite', token], queryFn: () => api.get(`/auth/invitations/${token}`), enabled: token.length > 20, retry: false });
  const [fullName, setFullName] = useState('');
  const [pw, setPw] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  return (
    <AuthLayout>
      <h1 className="cond text-[28px] font-semibold">Join your team</h1>
      {info.isLoading && <Spinner />}
      {(info.error || !token) && <div className="mt-4"><ErrorBanner error={info.error ?? new ApiError(404, 'NOT_FOUND', 'This invitation link is invalid or has expired. Ask your administrator to send a new one.')} /></div>}
      {info.data && (
        <form
          className="mt-6 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await api.post('/auth/invitations/accept', { token, fullName: fullName || undefined, password: pw });
              await refresh();
              nav('/', { replace: true });
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <p className="text-ink-2">
            <strong>{info.data.orgName}</strong> invited <strong>{info.data.email}</strong> as {info.data.roleName}.
          </p>
          {!info.data.existingUser && <TextField label="Your full name" value={fullName || info.data.fullName || ''} onChange={setFullName} required />}
          <TextField
            label={info.data.existingUser ? 'Your existing password' : 'Choose a password'}
            type="password"
            hint={info.data.existingUser ? 'You already have an account — confirm it to add this organisation.' : 'At least 10 characters with letters and numbers'}
            value={pw}
            onChange={setPw}
            required
          />
          <ErrorBanner error={error} />
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
            Accept invitation
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
